import { useState } from "react";
import { User, Lock, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useClientAuth } from "@/context/ClientAuthContext";
import { PortalHeader } from "@/pages/client/_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function Section({ icon: Icon, title, children }) {
  return (
    <div className="rounded-2xl border border-white/8 bg-[#121214] p-6 mb-5">
      <div className="flex items-center gap-2.5 mb-5"><Icon className="w-5 h-5 text-[#D4AF37]" /><h3 className="text-white font-medium">{title}</h3></div>
      {children}
    </div>
  );
}

export default function ClientSettings() {
  const { client, setClient, refresh } = useClientAuth();
  const [profile, setProfile] = useState({ name: client?.name || "", company: client?.company || "", business_type: client?.business_type || "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [twofa, setTwofa] = useState(null);
  const [code, setCode] = useState("");

  const saveProfile = async (e) => {
    e.preventDefault();
    try { const { data } = await api.put("/portal/profile", profile); setClient(data); toast.success("Profile updated"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const changePw = async (e) => {
    e.preventDefault();
    try { await api.post("/portal/auth/change-password", pw); toast.success("Password updated"); setPw({ current_password: "", new_password: "" }); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const setup2fa = async () => {
    try { const { data } = await api.post("/portal/auth/2fa/setup"); setTwofa(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const enable2fa = async (e) => {
    e.preventDefault();
    try { await api.post("/portal/auth/2fa/enable", { code }); toast.success("2FA enabled"); setTwofa(null); setCode(""); refresh(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const disable2fa = async () => {
    const c = window.prompt("Enter a current 2FA code to disable two-factor authentication");
    if (!c) return;
    try { await api.post("/portal/auth/2fa/disable", { code: c }); toast.success("2FA disabled"); refresh(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="fv-fade-up">
      <PortalHeader title="Profile & Settings" subtitle="Manage your account details and security." />

      <Section icon={User} title="Business profile">
        <form onSubmit={saveProfile} className="space-y-4" data-testid="client-profile-form">
          <div><Label className="text-[#D4D4D8] mb-1.5 block text-sm">Name</Label>
            <Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} data-testid="client-profile-name" className="bg-[#0d0d0f] border-white/10 text-white" /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-[#D4D4D8] mb-1.5 block text-sm">Company</Label>
              <Input value={profile.company} onChange={(e) => setProfile({ ...profile, company: e.target.value })} data-testid="client-profile-company" className="bg-[#0d0d0f] border-white/10 text-white" /></div>
            <div><Label className="text-[#D4D4D8] mb-1.5 block text-sm">Business type</Label>
              <Input value={profile.business_type} onChange={(e) => setProfile({ ...profile, business_type: e.target.value })} data-testid="client-profile-type" className="bg-[#0d0d0f] border-white/10 text-white" /></div>
          </div>
          <div><Label className="text-[#D4D4D8] mb-1.5 block text-sm">Email</Label>
            <Input value={client?.email || ""} disabled className="bg-[#0d0d0f] border-white/10 text-[#71717A]" /></div>
          <Button type="submit" data-testid="client-profile-save" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-lg">Save changes</Button>
        </form>
      </Section>

      <Section icon={Lock} title="Change password">
        <form onSubmit={changePw} className="space-y-4" data-testid="client-password-form">
          <div><Label className="text-[#D4D4D8] mb-1.5 block text-sm">Current password</Label>
            <Input type="password" required value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="client-pw-current" className="bg-[#0d0d0f] border-white/10 text-white" /></div>
          <div><Label className="text-[#D4D4D8] mb-1.5 block text-sm">New password</Label>
            <Input type="password" required value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="client-pw-new" className="bg-[#0d0d0f] border-white/10 text-white" /></div>
          <Button type="submit" data-testid="client-pw-save" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-lg">Update password</Button>
        </form>
      </Section>

      <Section icon={ShieldCheck} title="Two-factor authentication">
        {client?.two_fa_enabled ? (
          <div className="flex items-center justify-between">
            <span className="text-sm text-emerald-400">✓ Two-factor authentication is enabled</span>
            <Button variant="outline" onClick={disable2fa} data-testid="client-2fa-disable" className="border-white/15 bg-transparent text-white rounded-lg hover:bg-white/5">Disable</Button>
          </div>
        ) : twofa ? (
          <form onSubmit={enable2fa} className="space-y-4" data-testid="client-2fa-enable-form">
            <p className="text-sm text-[#A1A1AA]">Scan this QR code with your authenticator app, then enter the 6-digit code.</p>
            <img src={twofa.qr_data_url} alt="2FA QR" className="w-40 h-40 rounded-lg bg-white p-2" />
            <Input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456"
              data-testid="client-2fa-code" className="bg-[#0d0d0f] border-white/10 text-white max-w-[200px] tracking-[0.3em] text-center" />
            <Button type="submit" data-testid="client-2fa-confirm" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-lg">Verify & enable</Button>
          </form>
        ) : (
          <div className="flex items-center justify-between">
            <span className="text-sm text-[#A1A1AA]">Add an extra layer of security to your account.</span>
            <Button onClick={setup2fa} data-testid="client-2fa-setup" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-lg">Enable 2FA</Button>
          </div>
        )}
      </Section>
    </div>
  );
}
